import type { SliceFactoryPropsWithMessages } from 'src/store/shared/types';
import { createSlice } from '@reduxjs/toolkit';
import { parseResponse } from '@skynet/data-ws-core';
import type {
  JsonChartCandlesData,
  JsonChartSuccess,
  TradingViewState,
} from 'src/store/shared/trading-view/types';
import { TradingViewCommands } from 'src/store/shared/trading-view/types';

export function initialTradingViewSlice({
  sliceName = 'tradingView',
  prefix,
  commands = TradingViewCommands,
  initialState,
  messages,
  extraReducers,
}: SliceFactoryPropsWithMessages<TradingViewState, typeof TradingViewCommands>) {
  const allowedCommands = Object.values(commands);
  if (!initialState) {
    throw new Error('Invalid initial state for trading view slice');
  }
  return createSlice({
    name: `${prefix}/${sliceName}`,
    initialState,
    reducers: {
      resetState: () => initialState,
    },
    extraReducers: (builder) => {
      if (typeof extraReducers === 'function') {
        extraReducers(builder);
      }

      builder.addCase(messages, (state, action) => {
        const { json, skipProcessing, error, errorCommand } = parseResponse(
          action.payload,
          allowedCommands,
        );

        if (skipProcessing || !json) return;

        console.debug(
          `${prefix}/${sliceName}/processing ${messages.toString()}`,
          action,
          allowedCommands,
        );

        const { command } = json;

        if (error) {
          state.error = error;

          switch (errorCommand) {
            case undefined:
            default:
          }

          return;
        }

        if (command === TradingViewCommands.Chart) {
          const { chart } = json as JsonChartSuccess;
          const jsonCandlesData: JsonChartCandlesData[] =
            chart?.find((entry) => entry.type === 'candles')?.data ?? [];
          state.data = jsonCandlesData.map((candle) => ({
            time: candle.d.split(' ')[0],
            open: candle.o,
            high: candle.h,
            low: candle.l,
            close: candle.c,
            value: Number(candle.v),
          }));
          state.status = 'success';
        }
      });
    },
  });
}
